/**
 * Patient ↔ CPT Exam Validation Redux Slice.
 *
 * Manages validation state: issues, loading, blocking status.
 * Provides async thunk to call the validation API and actions
 * to clear state when patient/exam selection changes.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import validationService from '../services/validationService';
import type {
  PatientExamValidationRequest,
  PatientExamValidationResponse,
  ValidationIssue,
} from '../services/validationService';

export interface ValidationState {
  /** Whether validation is currently running */
  loading: boolean;
  /** Most recent validation result */
  result: PatientExamValidationResponse | null;
  /** Convenience: true if any CRITICAL issue exists (blocks actions) */
  blocked: boolean;
  /** Convenience: all current issues */
  issues: ValidationIssue[];
  /** Error message if validation API call failed */
  error: string | null;
}

const initialState: ValidationState = {
  loading: false,
  result: null,
  blocked: false,
  issues: [],
  error: null,
};

// ── Async Thunk ─────────────────────────────────────────────────────

export const validatePatientExam = createAsyncThunk(
  'validation/validatePatientExam',
  async (request: PatientExamValidationRequest) => {
    const response = await validationService.validatePatientExam(request);
    return response;
  },
);

// ── Slice ───────────────────────────────────────────────────────────

const validationSlice = createSlice({
  name: 'validation',
  initialState,
  reducers: {
    clearValidation(state) {
      state.result = null;
      state.blocked = false;
      state.issues = [];
      state.error = null;
      state.loading = false;
    },
    /** Set validation result from local (non-API) validation */
    setValidationResult(state, action: PayloadAction<PatientExamValidationResponse>) {
      state.result = action.payload;
      state.issues = action.payload.issues;
      state.blocked = action.payload.issues.some((i) => i.severity === 'critical');
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validatePatientExam.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validatePatientExam.fulfilled, (state, action) => {
        state.loading = false;
        state.result = action.payload;
        state.issues = action.payload.issues;
        state.blocked = action.payload.issues.some((i) => i.severity === 'critical');
        state.error = null;
      })
      .addCase(validatePatientExam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Validation failed';
        // Don't block on API errors — fail open
        state.blocked = false;
      });
  },
});

export const { clearValidation, setValidationResult } = validationSlice.actions;
export default validationSlice.reducer;
